import { FormData } from '../types/forms'

const CACHE_KEY_PREFIX = 'lts_form_cache_'

function getCacheKey(formName: string, userId: string, instanceId?: string): string {
  if (instanceId) return `${CACHE_KEY_PREFIX}${formName}_${userId}_${instanceId}`
  return `${CACHE_KEY_PREFIX}${formName}_${userId}`
}

/** Flatten all form fields (including those inside groups) for cache filtering */
function getAllFormFieldsFlat(form: FormData): Array<{ id: string; excludeFromCache?: boolean; readonly?: boolean; fillerStage?: number }> {
  const fields: Array<{ id: string; excludeFromCache?: boolean; readonly?: boolean; fillerStage?: number }> = []
  const walk = (q: any) => {
    if (!q) return
    if (q.type === 'group') {
      // For nested groups, recurse into group questions.
      for (const cq of q.questions || []) walk(cq)
      // Radio groups store the selected value at group id, not child ids.
      if (q.mode === 'radio' && q.id) {
        fields.push({ id: q.id, excludeFromCache: q.excludeFromCache, readonly: q.readonly, fillerStage: q.fillerStage })
      }
      return
    }
    if (q.id) {
      fields.push({ id: q.id, excludeFromCache: q.excludeFromCache, readonly: q.readonly, fillerStage: q.fillerStage })
    }
  }

  for (const step of form.steps || []) {
    for (const q of step.questions || []) walk(q)
  }
  return fields
}

export type FormCacheOptions = {
  instanceId?: string
  currentStage?: number | null
}

function allowedCacheIds(form: FormData, options?: FormCacheOptions): Set<string> {
  const fields = getAllFormFieldsFlat(form)
  return new Set(
    fields
      .filter((f) => !f.excludeFromCache)
      .filter((f) => {
        if (options?.currentStage == null) return !f.readonly
        const stage = Number(f.fillerStage) >= 1 ? Number(f.fillerStage) : 1
        return stage === options.currentStage
      })
      .map((f) => f.id)
  )
}

/**
 * Load cached answers for a form. Returns null if cache is disabled, expired, or missing.
 * Filters to only include fields that exist in the form and are not excluded from cache.
 * Never restores locked other-stage fields. Readonly current-stage fields may restore tool fills.
 */
export function loadCachedAnswers(
  formName: string,
  userId: string | undefined,
  form: FormData,
  options?: FormCacheOptions,
): Record<string, any> | null {
  if (!form?.cache?.enabled || !userId) return null

  const key = getCacheKey(formName, userId, options?.instanceId)
  try {
    const raw = localStorage.getItem(key)
    if (!raw) return null

    const parsed = JSON.parse(raw) as { cachedAt: number; answers: Record<string, any> }
    if (!parsed?.cachedAt || !parsed?.answers) return null

    const expiryMs = (form.cache.expiryHours ?? 24) * 60 * 60 * 1000
    if (Date.now() - parsed.cachedAt > expiryMs) {
      localStorage.removeItem(key)
      return null
    }

    const allowedIds = allowedCacheIds(form, options)
    const filtered: Record<string, any> = {}
    for (const [id, value] of Object.entries(parsed.answers)) {
      if (allowedIds.has(id)) filtered[id] = value
    }
    return filtered
  } catch {
    return null
  }
}

/**
 * Save answers to cache. Only stores fields that are not excluded from cache.
 */
export function saveCachedAnswers(
  formName: string,
  userId: string | undefined,
  answers: Record<string, any>,
  form: FormData,
  options?: FormCacheOptions,
): void {
  if (!form?.cache?.enabled || !userId) return

  const allowedIds = allowedCacheIds(form, options)
  const filtered: Record<string, any> = {}
  for (const [id, value] of Object.entries(answers)) {
    if (allowedIds.has(id)) filtered[id] = value
  }

  const key = getCacheKey(formName, userId, options?.instanceId)
  try {
    localStorage.setItem(
      key,
      JSON.stringify({ cachedAt: Date.now(), answers: filtered })
    )
  } catch {
    // localStorage full or unavailable - ignore
  }
}

/**
 * Clear cached answers for a form (e.g. after successful submit).
 */
export function clearCachedAnswers(
  formName: string,
  userId: string | undefined,
  instanceId?: string,
): void {
  if (!userId) return
  try {
    localStorage.removeItem(getCacheKey(formName, userId, instanceId))
  } catch {
    // ignore
  }
}
